import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link as MuiLink,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import { Visibility, VisibilityOff, Login, Email, Lock } from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTenant } from '../../context/TenantContext';
import { signInWithMicrosoft } from '../../services/authService';

// Error messages for different error codes
const ERROR_MESSAGES: Record<string, string> = {
  domain_restricted: 'Access restricted to authorized company accounts only. Please use your @gargash.ae email.',
  access_denied: 'Access denied. Your account is not authorized for this application. Please contact your administrator.',
  auth_error: 'Authentication failed. Please try again or contact support.',
  session_expired: 'Your session has expired. Please sign in again.',
  unauthorized: 'You are not authorized to access this application.'
};

export const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { currentTenant } = useTenant();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();

  // Check for error query parameter on mount (e.g., domain_restricted)
  useEffect(() => {
    const errorCode = searchParams.get('error');
    if (errorCode && ERROR_MESSAGES[errorCode]) {
      setError(ERROR_MESSAGES[errorCode]);
      // Clear the error from URL to prevent showing on refresh
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Real Supabase authentication
      const { signIn } = await import('../../services/authService');
      const result = await signIn(formData.email, formData.password);

      if (result.success) {
        // Successful login
        navigate('/admin/dashboard');
      } else {
        setError(result.message || 'Invalid email or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsLoading(true);
    setError('');

    const result = await signInWithMicrosoft();

    if (!result.success) {
      setError('Failed to sign in with Microsoft. Please try again.');
      setIsLoading(false);
    }
    // If successful, user will be redirected to Microsoft login
    // After authentication, they'll be redirected back to dashboard
  };

  const isFormValid = formData.email && formData.password;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.main} 100%)`,
        p: 2,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 60%)',
          pointerEvents: 'none',
        }
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={24}
          sx={{
            p: 5,
            borderRadius: 4,
            textAlign: 'center',
            backdropFilter: 'blur(10px)',
            background: 'rgba(255, 255, 255, 0.98)',
            boxShadow: '0 20px 60px rgba(83, 46, 121, 0.3)'
          }}
        >
          {/* Logo and Company Name */}
          <Box sx={{ mb: 5 }}>
            {currentTenant?.theme.logoUrl && (
              <img
                src={currentTenant.theme.logoUrl}
                alt={`${currentTenant.name} logo`}
                style={{ height: 56, marginBottom: 20 }}
              />
            )}
            <Typography variant="h3" component="h1" gutterBottom sx={{
              fontWeight: 800,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 1
            }}>
              Admin Portal
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
              {currentTenant?.name || 'HR Portal'}
            </Typography>
          </Box>

          {/* Demo Credentials */}
          <Alert severity="info" sx={{
            mb: 4,
            textAlign: 'left',
            borderRadius: 3,
            border: '2px solid',
            borderColor: 'info.light',
            backgroundColor: alpha(theme.palette.info.main, 0.08)
          }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 700 }}>
              🔑 Test Credentials
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
              Use any user created in Supabase<br />
              Example: admin@techcorp.com
            </Typography>
          </Alert>

          {error && (
            <Alert severity="error" sx={{
              mb: 3,
              borderRadius: 3,
              border: '2px solid',
              borderColor: 'error.light',
              backgroundColor: alpha(theme.palette.error.main, 0.08)
            }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              required
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              required
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={!isFormValid || isLoading}
              startIcon={<Login />}
              sx={{
                py: 1.5,
                mb: 3
              }}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>

            {/* Divider */}
            <Divider sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>

            {/* Microsoft SSO Button */}
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleMicrosoftLogin}
              disabled={isLoading}
              sx={{
                py: 1.5,
                mb: 3,
                borderColor: '#0078d4',
                color: '#0078d4',
                '&:hover': {
                  borderColor: '#106ebe',
                  backgroundColor: 'rgba(0, 120, 212, 0.04)'
                }
              }}
              startIcon={
                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
              }
            >
              Sign in with Microsoft
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <MuiLink
                component={Link}
                to="/"
                variant="body2"
                sx={{ textDecoration: 'none' }}
              >
                ← Back to Main Site
              </MuiLink>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};